import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Panel } from '@/shared/ui';
import type { StationFormApi } from '../useStationForm';
import type { PlaceEditor } from '../usePlaceEditor';
import { PlaceRow } from './PlaceRow';

export interface StationDetailsPanelProps {
  form: StationFormApi;
  editor: PlaceEditor;
}

/** Panel se seznamem nástupních míst — přidávání, drag & drop přeřazení a rozbalitelná editace. */
export function StationDetailsPanel({ form, editor }: StationDetailsPanelProps) {
  const { t } = useTranslation('products');
  const {
    dragIndex,
    dragOverIndex,
    setDragIndex,
    setDragOverIndex,
    setMapMode,
    fit,
    reorder,
    moveToTop,
    removePlace,
    addPlace,
    expandedKey,
    toggleExpand,
    setCoord,
  } = editor;

  return (
    <Panel
      variant="filled"
      icon="pin"
      collapsible
      title={t('stationDetail.panelTitle')}
      badge={
        <form.Subscribe selector={(state) => state.values.stationDetails.length}>
          {(count) =>
            count > 0 ? <span className="text-phx-sm text-phx-text-muted">{count}</span> : null
          }
        </form.Subscribe>
      }
      actions={
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconLeft="pin"
            onClick={() => setMapMode({ kind: 'new-place' })}
          >
            {t('stationDetail.fromMap')}
          </Button>
          <Button type="button" variant="secondary" size="sm" iconLeft="plus" onClick={addPlace}>
            {t('stationDetail.add')}
          </Button>
        </>
      }
      footer={
        <form.Subscribe selector={(state) => state.values.stationDetails.length}>
          {(count) => (
            <span className="text-phx-text-muted text-phx-xs">
              {t('stationDetail.footer', { count })}
            </span>
          )}
        </form.Subscribe>
      }
    >
      <form.Subscribe selector={(state) => state.values.stationDetails}>
        {(details) =>
          details.length === 0 ? (
            <p className="text-phx-text-muted text-phx-sm p-phx-2">{t('stationDetail.empty')}</p>
          ) : (
            <div className="gap-phx-2 flex flex-col">
              {details.map((place, index) => (
                <Fragment key={place.key}>
                  <DropIndicator
                    visible={dragIndex !== null && dragOverIndex === index && dragIndex !== index}
                  />
                  <PlaceRow
                    place={place}
                    index={index}
                    expanded={expandedKey === place.key}
                    dragging={dragIndex === index}
                    form={form}
                    onToggle={() => toggleExpand(place.key)}
                    onDragStart={() => setDragIndex(index)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onDragOver={(event) => {
                      if (dragIndex !== null) {
                        event.preventDefault();
                        setDragOverIndex(index);
                      }
                    }}
                    onDrop={() => {
                      if (dragIndex !== null) reorder(dragIndex, index);
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onSetMain={() => moveToTop(index)}
                    onShowOnMap={fit}
                    onRemove={() => removePlace(index)}
                    onChangeInMap={() => setMapMode({ kind: 'edit-place', key: place.key })}
                    onCoord={(prop, raw) => setCoord(`stationDetails[${index}].${prop}`, raw)}
                  />
                </Fragment>
              ))}
              {/* Drop zóna na konec seznamu (přesun za poslední). */}
              {dragIndex !== null ? (
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOverIndex(details.length);
                  }}
                  onDrop={() => {
                    reorder(dragIndex, details.length);
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  className="py-phx-1"
                >
                  <DropIndicator visible={dragOverIndex === details.length} />
                </div>
              ) : null}
            </div>
          )
        }
      </form.Subscribe>
    </Panel>
  );
}

/** Vizualizace místa dropu při drag & drop přeřazování nástupních míst. */
function DropIndicator({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }
  return (
    <div
      aria-hidden
      className="bg-phx-primary -my-px h-0.5 rounded-full"
      data-testid="drop-indicator"
    />
  );
}
